import * as React from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useBreakpoint } from "../../hooks/useBreakpoint";

interface ResultsViewToggleProps {
  mode: string;
  onModeChange: (mode: string) => void;
  options: { label: string; icon: React.JSX.Element }[];
  disabled?: boolean;
  dropDownOptions?: { label: string; value: string }[];
  dropDown?: string;
  setDropDown?: (string: string) => void;
  backToNational?: boolean;
  onBackToNational?: () => void;
}

export default function ResultsViewToggle({
  mode,
  onModeChange,
  options,
  disabled = false,
  dropDownOptions,
  dropDown,
  setDropDown,
  backToNational = false,
  onBackToNational,
}: ResultsViewToggleProps) {
  const [leftOption, ...rightOptions] = options;
  const { smallScreen } = useBreakpoint();

  const renderToggle = (option: { label: string; icon: React.JSX.Element }) => {
    const textColor = "#4c75b7";
    const isSelected = mode === option.label;
    const isClickable = !disabled && !isSelected;

    return (
      <ToggleButton
        key={option.label}
        value={option.label}
        selected={mode === option.label}
        disabled={disabled}
        onChange={() => {
          if (isClickable) onModeChange(option.label);
        }}
        aria-label={`${option.label} view`}
        sx={{
          "border": "1px solid transparent",
          "borderRadius": "8px",
          "px": 2,
          "py": 0.75,
          "padding": 0,
          "minWidth": 90,
          "height": 35,
          "textTransform": "none",
          "color": textColor,
          "& .MuiSvgIcon-root": {
            color: textColor,
            ml: "6px",
            fontSize: 22,
          },
          "& .MuiTypography-root": {
            color: textColor,
            fontWeight: 700,
          },
          "&.Mui-selected": {
            backgroundColor: textColor,
            borderColor: "transparent",
          },
          "&.Mui-selected .MuiTypography-root": {
            color: "#ffffff",
          },
          "&.Mui-selected .MuiSvgIcon-root": {
            color: "#ffffff",
          },
          "&:hover": {
            backgroundColor: "#ffffff",
            borderColor: "transparent",
          },
          "&.Mui-selected:hover": {
            backgroundColor: isClickable ? "#3d669e" : textColor,
          },
          "cursor": isClickable ? "pointer" : "default",
        }}
      >
        <Typography>
          {option.label.replace(/^./, (c) => c.toUpperCase())}
        </Typography>
        {option.icon}
      </ToggleButton>
    );
  };

  return (
    <Box sx={{ display: "inline-flex" }} id="view-results-toggle" width="100%">
      {smallScreen ? (
        <Box
          sx={{
            alignItems: "stretch",
            border: `1px solid #adc7df`,
            borderRadius: "8px",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Left section: View Results + first view option */}
          <Box
            sx={{
              backgroundColor: "#eef4fc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              px: 1,
              py: 1,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  pb: 0,
                  color: "#1A4480",
                  lineHeight: 1,
                }}
              >
                View
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  pb: 0,
                  color: "#1A4480",
                  lineHeight: 1,
                }}
              >
                Results:
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              {options.map((opt) => renderToggle(opt))}
            </Box>
            {/* Right section: map + back to national button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1,
                py: 1,
              }}
            >
              {backToNational && onBackToNational && (
                <Button
                  onClick={onBackToNational}
                  variant="contained"
                  disableElevation
                  startIcon={
                    <ArrowBackIosNewIcon
                      sx={{
                        fontSize: "14px !important",
                      }}
                    />
                  }
                  sx={{
                    "backgroundColor": "#ffffff",
                    "color": "#1A4480",
                    "fontWeight": 700,
                    "textTransform": "none",
                    "borderRadius": "8px",
                    "height": 35,
                    "px": 2.5,
                    "minWidth": 120,
                    "&&": { margin: "0 !important" },
                    "&:hover": {
                      "backgroundColor": "#1a4480 !important",
                      "color": "#ffffff !important",
                      "borderColor": "#1a4480 !important",
                      "& .MuiSvgIcon-root": { color: "#ffffff !important" },
                    },
                  }}
                >
                  National
                </Button>
              )}
            </Box>
          </Box>

          {dropDownOptions && setDropDown && dropDown && (
            <Paper
              sx={{
                backgroundColor: "#4b76b7",
                borderRadius: "0 0 8px 8px",
                boxShadow: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 1,
              }}
            >
              <FormControl sx={{ width: 340 }} size="small">
                <Select
                  id="results-toggle-dropdown"
                  value={dropDown}
                  onChange={(event) => setDropDown(event.target.value)}
                  displayEmpty
                  sx={{
                    "height": 30,
                    "mx": 2,
                    "borderRadius": 0,
                    "padding": 0,
                    "& .MuiSelect-select": {
                      padding: "0 14px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: (theme) => ({
                        "borderRadius": 0,
                        "& .MuiList-root": {
                          padding: 0,
                        },

                        "& .MuiMenuItem-root": {
                          height: 30,
                          color: theme.palette.primary.main,
                        },
                      }),
                    },
                  }}
                >
                  {dropDownOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            border: `1px solid #adc7df`,
            borderRadius: "8px",
            overflow: "hidden",
            width: "fit-content",
          }}
        >
          {/* Left section: View Results + first view option */}
          <Box
            sx={{
              backgroundColor: "#eef4fc",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1,
              py: 1,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  pb: 0,
                  color: "#1A4480",
                  lineHeight: 1,
                }}
              >
                View
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  pb: 0,
                  color: "#1A4480",
                  lineHeight: 1,
                }}
              >
                Results:
              </Typography>
            </Box>

            {leftOption && renderToggle(leftOption)}
          </Box>

          {/* Right section: map + back to national button */}
          <Box
            sx={{
              backgroundColor:
                mode === "map" && backToNational ? "#4c75b7" : "#eef4fc",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1,
              py: 1,
            }}
          >
            {rightOptions.map((opt) => renderToggle(opt))}

            {backToNational && onBackToNational && (
              <Button
                onClick={onBackToNational}
                variant="contained"
                disableElevation
                startIcon={
                  <ArrowBackIosNewIcon
                    sx={{
                      fontSize: "14px !important",
                    }}
                  />
                }
                sx={{
                  "backgroundColor": "#ffffff",
                  "color": "#1A4480",
                  "fontWeight": 700,
                  "textTransform": "none",
                  "borderRadius": "8px",
                  "height": 35,
                  "px": 2.5,
                  "minWidth": 120,
                  "&&": { margin: "0 !important" },
                  "&:hover": {
                    "backgroundColor": "#1a4480 !important",
                    "color": "#ffffff !important",
                    "borderColor": "#1a4480 !important",
                    "& .MuiSvgIcon-root": { color: "#ffffff !important" },
                  },
                }}
              >
                National
              </Button>
            )}
          </Box>

          {dropDownOptions && setDropDown && dropDown && (
            <Paper
              sx={{
                backgroundColor: "#4b76b7",
                borderRadius: "0 8px 8px 0",
                boxShadow: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FormControl sx={{ width: 340 }} size="small">
                <Select
                  id="results-toggle-dropdown"
                  value={dropDown}
                  onChange={(event) => setDropDown(event.target.value)}
                  displayEmpty
                  sx={{
                    "height": 30,
                    "mx": 2,
                    "borderRadius": 0,
                    "padding": 0,
                    "& .MuiSelect-select": {
                      padding: "0 14px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: (theme) => ({
                        "borderRadius": 0,
                        "& .MuiList-root": {
                          padding: 0,
                        },

                        "& .MuiMenuItem-root": {
                          height: 30,
                          color: theme.palette.primary.main,
                        },
                      }),
                    },
                  }}
                >
                  {dropDownOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
